/**
 * Escala de estrellas y calificación de los juegos (SCRUM-645).
 *
 * Conviven dos contratos:
 *
 *   v1.15   El juego reporta una calificación sobre 10 —10, 8, 6 o 4— derivada
 *           del porcentaje de interacciones resueltas sin error de aprendizaje.
 *           El niño la ve como estrellas: 10 → 3, 8 → 2, 6 → 1, 4 → 0. El piso
 *           es 4: cero estrellas es un resultado real y visible, no un cero.
 *
 *   anterior El template reportaba `{ stars, struggled_interactions }` y las
 *           estrellas salían de cuántas interacciones distintas costaron a la
 *           primera. NO lleva calificación. En producción no hay ninguna
 *           partida con este contrato (2026-10-19): los juegos de Kinder no
 *           reportan resultado y solo queda la primera vez que se terminaron
 *           (SCRUM-1167).
 *
 * Vive acá, y no dentro del componente, para poder verificar las escalas contra
 * el contrato sin montar la página.
 */

export const MAX_STARS = 3;

/** Calificaciones que reporta el motor, ascendente: el índice son las estrellas. */
export const ALLOWED_EVALUATIONS = [4, 6, 8, 10];

/**
 * Interacciones con dificultad a partir de las cuales el template ANTERIOR
 * disparaba Game Over. El contrato v1.15 no tiene Game Over: el niño siempre
 * llega a la tarjeta de resultados.
 */
export const GAME_OVER_STRUGGLED = 5;

/** Explicación de un valor de estrellas, para el tooltip de la tabla. */
export function describeStars(stars: number, evaluation?: number | null): string {
  if (evaluation !== null && evaluation !== undefined) {
    return `Calificación ${evaluation}/10 · ${describeEvaluation(evaluation)}`;
  }

  switch (stars) {
    case 3:
      return "0 o 1 interacción con dificultad";
    case 2:
      return "2 o 3 interacciones con dificultad";
    case 1:
      return "4 interacciones con dificultad";
    default:
      // En el contrato anterior, 0 estrellas no existía: el template hacía Game
      // Over antes de llegar a esa nota.
      return "Fuera de la escala del template anterior";
  }
}

/** Franja de aciertos limpios detrás de una calificación, según el contrato. */
export function describeEvaluation(evaluation: number): string {
  switch (evaluation) {
    case 10:
      return "85 % o más de aciertos limpios";
    case 8:
      return "entre 70 % y 85 %";
    case 6:
      return "entre 55 % y 70 %";
    case 4:
      return "menos de 55 %";
    default:
      return "fuera de la escala del motor";
  }
}

/** Color del Tag según la nota, dentro de la paleta semántica de antd. */
export function starTagColor(stars: number): string {
  if (stars >= MAX_STARS) return "green";
  if (stars === 2) return "gold";
  if (stars === 1) return "orange";
  return "red";
}

/**
 * Estrellas que corresponden a una calificación del contrato v1.15. Se usa para
 * verificar la escala; la nota real la manda el juego.
 */
export function starsForEvaluation(evaluation: number): number | null {
  const stars = ALLOWED_EVALUATIONS.indexOf(evaluation);
  return stars === -1 ? null : stars;
}

/**
 * Estrellas que el template ANTERIOR asignaría para una cantidad de
 * interacciones con dificultad. Se usa para verificar la escala.
 */
export function expectedStarsFor(struggledInteractions: number): number | null {
  if (struggledInteractions < 0) return null;
  if (struggledInteractions <= 1) return 3;
  if (struggledInteractions <= 3) return 2;
  if (struggledInteractions === 4) return 1;
  // 5 o más: Game Over, la partida no reporta.
  return null;
}
